import type { StaffOrderDetail } from "@/api/staff";
import { useNavigate } from "react-router-dom";

interface Props {
  orderInfo: StaffOrderDetail;
  orderId: number;
  onUpdate?: () => void;
}

interface PunchRecord {
  type: string;
  punchType?: string; // 跳转打卡页的 type 参数
  status: "done" | "pending" | "new";
  time?: string;
  lines?: string[];
  img?: string;
  color: string;
}

export function PunchTimelineSection({ orderInfo, orderId }: Props) {
  const navigate = useNavigate();

  const depart = orderInfo.is_depart ?? 0;
  const st = orderInfo.status ?? 0;

  /**
   * 接口图片字段说明（来自 punching/page.tsx 已有逻辑）：
   *   sign_imgs    → 服务开始打卡的图片
   *   staff_imgs   → 服务完成打卡的图片
   *
   * 出发/到达两个节点接口无独立图片字段，不显示图。
   */

  // 构建打卡记录列表（按原型图顺序）
  const records: PunchRecord[] = [];

  // 1. 出发打卡（签约完成后可操作）
  if (depart >= 1) {
    records.push({
      type: "出发打卡",
      punchType: "depart",
      status: "done",
      time: orderInfo.depart_time_desc || undefined,
      lines: [
        orderInfo.depart_addr || "",
        "已确认出发，前往客户指定地点",
      ],
      // 接口无出发打卡独立图片字段
      color: "#7C4DFF",
    });
  } else {
    records.push({
      type: "出发打卡", punchType: "depart",
      status: st >= 16 ? "new" : "pending", color: "#7C4DFF",
    });
  }

  // 2. 到达打卡（已出发后可操作）
  if (depart >= 2) {
    records.push({
      type: "到达打卡",
      punchType: "arrive",
      status: "done",
      time: orderInfo.arrive_time_desc || undefined,
      lines: [orderInfo.arrive_addr || "", "已到达该地点，等待客户"],
      // 接口无到达打卡独立图片字段
      color: "#2979FF",
    });
  } else {
    records.push({
      type: "到达打卡", punchType: "arrive",
      status: (depart === 1 && st >= 16) ? "new" : "pending", color: "#2979FF",
    });
  }

  // 3. 服务开始打卡（已到达且已开始服务）— 图片用 sign_imgs
  const serviceStarted = depart >= 3 && st >= 30;
  if (serviceStarted) {
    records.push({
      type: "服务开始",
      punchType: "start",
      status: "done",
      time: orderInfo.start_time_desc || orderInfo.sign_time_desc || undefined,
      lines: [orderInfo.start_addr || "", orderInfo.sign_content || "已确认身份，服务正式开始"],
      img: (orderInfo.sign_imgs?.length ? orderInfo.sign_imgs[0] : undefined),
      color: "#00BFA5",
    });
  } else {
    const canStart = depart >= 2 && st === 20;
    records.push({
      type: "服务开始", punchType: "start",
      status: canStart ? "new" : "pending", color: "#00BFA5",
    });
  }

  // 4. 服务完成打卡（进行中状态可操作）— 图片用 staff_imgs
  const isFinishDone = st >= 40;
  records.push({
    type: "服务完成",
    punchType: "finish",
    status: isFinishDone ? "done" : (st === 30 ? "new" : "pending"),
    time: orderInfo.finish_time_desc || undefined,
    lines: isFinishDone ? ([orderInfo.finish_addr || "", orderInfo.staff_remark || "服务已完成"]) : undefined,
    img: isFinishDone ? (orderInfo.staff_imgs?.[0] || undefined) : undefined,
    color: "#FF8A65",
  });

  const doneCount = records.filter((r) => r.status === "done").length;
  const totalCount = records.length;

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
      {/* 头部 */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "#EDE7F6" }}>
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: "#7C4DFF" }} />
          </div>
          <span className="text-sm font-bold text-gray-900">达人打卡服务</span>
        </div>
        <span className="text-xs" style={{ color: "#7C4DFF" }}>{doneCount}/{totalCount} 已完成</span>
      </div>

      <div className="px-4 pb-3 text-xs text-gray-400 leading-relaxed">
        请在抵达客户指定地点后拍照打卡，信息将展示给用户供其核验。
      </div>

      {/* 打卡时间线 */}
      <div className="px-4 pb-4 space-y-3">
        {records.map((rec, i) => (
          <PunchRecordItem
            key={i}
            record={rec}
            onAction={
              rec.status === "new" && rec.punchType
                ? () => navigate(`/workspace/order/punch-depart?id=${orderId}&type=${rec.punchType}`)
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

function PunchRecordItem({ record, onAction }: { record: PunchRecord; onAction?: () => void }) {
  // 未完成时统一灰色，已完成才显示彩色
  const isActive = record.status === "done";
  const dotColor = isActive ? record.color : "#C0C0C0";

  return (
    <div className="flex gap-3">
      {/* 左侧圆点+连线 */}
      <div className="flex flex-col items-center pt-1">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
          style={{ background: `${dotColor}18` }}
        >
          <div className="w-3 h-3 rounded-full" style={{ background: dotColor }} />
        </div>
        {record.status !== "new" && (
          <div
            className="w-[2px] flex-1 min-h-[12px]"
            style={{ background: isActive ? `${dotColor}40` : "#EEE" }}
          />
        )}
      </div>

      {/* 右侧内容 */}
      <div className="flex-1 min-w-0 pb-1">
        {/* 标题行 + 状态标签 */}
        <div className="flex items-center justify-between mb-1.5">
          <span className={`text-sm font-medium ${isActive ? "text-gray-900" : "text-gray-400"}`}>{record.type}</span>
          {record.status === "done" && (
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ color: "#2979FF", background: "#E3F2FD" }}>
              已打卡
            </span>
          )}
          {record.status === "pending" && (
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ color: "#9CA3AF", background: "#F3F4F6" }}>
              待打卡
            </span>
          )}
        </div>

        {/* 时间 */}
        {record.time && (
          <p className="text-xs text-gray-400 mb-1.5">{record.time}</p>
        )}

        {/* 描述行 */}
        {record.lines?.map((line, li) => (
          <p key={li} className={`text-xs ${isActive ? (li === 0 ? "text-gray-500" : "text-gray-400") : "text-gray-300"} mb-0.5`}>
            <span className="inline-block w-1 h-1 rounded-full align-middle mr-1" style={{ background: dotColor }} />
            {line}
          </p>
        ))}

        {/* 图片 */}
        {record.img && (
          <img src={record.img} alt="" className="w-28 h-20 object-cover rounded-lg mt-2" />
        )}

        {/* 新打卡操作按钮 */}
        {record.status === "new" && onAction && (
          <button
            onClick={onAction}
            className="mt-3 w-fit py-2 px-6 rounded-full text-sm font-medium text-white active:scale-[0.98]"
            style={{ background: "#FE7A3A" }}
          >
            {record.punchType === "depart" ? "确认出发" : record.punchType === "arrive" ? "确认到达" : "立即开始拍照"}
          </button>
        )}
      </div>
    </div>
  );
}
